import { userStatusChecker, UserStatus } from './userStatusChecker';
import { flowRequirements } from './flowRequirements';
import { flowRegistry } from './flowRegistry';
import { greetingFlow } from '@/scripts/userflows/greetingFlow';

/**
 * Assistant Manager
 *
 * Orchestrates the entire assistant flow:
 * 1. Checks user status via APIs
 * 2. Determines what flow is needed
 * 3. Loads and returns the appropriate flow definitions
 *
 * This is the main entry point for initializing the assistant.
 *
 * Architecture:
 * - Always returns a greeting (shown first on login)
 * - Also returns pending flow if user needs to complete setup
 * - UI shows greeting → then pending flow (if any) → then closes
 */

export interface AssistantInitResult {
  // Greeting (always shown)
  greetingMessage: string;

  // User status (needed for flow engine to evaluate conditions)
  userStatus: UserStatus;

  // Pending flow (if user needs to complete something)
  flowId: string | null;
  flowName?: string;
  reason?: string;
  message?: string;
  flowDefinition: any | null;
}

class AssistantManager {
  /**
   * Initialize the assistant
   * Returns greeting + optional pending flow based on user status
   */
  async initializeAssistant(): Promise<AssistantInitResult> {
    try {
      console.log('[AssistantManager] Initializing assistant...');

      // Step 1: Always get greeting message
      const greetingMessage = this.getGreetingMessage();

      // Step 2: Check user status via APIs
      const status = await userStatusChecker.checkUserStatus();

      // Step 3: Determine what flow is needed based on status
      const nextFlow = flowRequirements.getNextFlow(status);

      // Step 4a: If no pending flows, return just greeting
      if (!nextFlow) {
        console.log('[AssistantManager] User has completed all setup - no pending flows');
        return {
          greetingMessage,
          userStatus: status,
          flowId: null,
          flowName: undefined,
          message: '✨ Everything is set up!',
          flowDefinition: null,
        };
      }

      // Step 4b: Get the pending flow definition
      console.log(`[AssistantManager] Next pending flow: ${nextFlow.flowId}`);

      const flowModule = flowRegistry.getFlow(nextFlow.flowId);

      if (!flowModule) {
        console.error(`[AssistantManager] Flow not found: ${nextFlow.flowId}`);
        return {
          greetingMessage,
          userStatus: status,
          flowId: null,
          message: 'Error: Flow not found',
          flowDefinition: null,
        };
      }

      return {
        greetingMessage,
        userStatus: status,
        flowId: nextFlow.flowId,
        flowName: nextFlow.flowName,
        reason: nextFlow.reason,
        message: this.getFlowInitMessage(nextFlow.flowId),
        flowDefinition: flowModule.definition,
      };
    } catch (error) {
      console.error('[AssistantManager] Error initializing assistant:', error);
      return {
        greetingMessage: 'Welcome back!',
        userStatus: {
          rolesSetup: { completed: false, rolesCount: 0, requiredCount: 2 },
          usersInvited: { completed: false, usersCount: 0, requiredCount: 1 },
        },
        flowId: null,
        message: 'Error initializing assistant. Please refresh and try again.',
        flowDefinition: null,
      };
    }
  }

  /**
   * Get greeting message for login
   * Picks a random variant from greetingFlow prompts
   */
  private getGreetingMessage(): string {
    const greetingPrompts = greetingFlow.definition.aiPrompts.login_greeting;
    if (greetingPrompts && greetingPrompts.variants && greetingPrompts.variants.length > 0) {
      const variants = greetingPrompts.variants as string[];
      return variants[Math.floor(Math.random() * variants.length)];
    }
    return 'Welcome to ESGCaffe! 👋';
  }

  /**
   * Get initial message for a flow
   */
  private getFlowInitMessage(flowId: string): string {
    const messages: Record<string, string> = {
      onboarding_flow: 'Let\'s get you set up!',
      esg_questionnaire_flow: 'Ready to fill your ESG data?',
      survey_flow: 'We\'d love your feedback!',
    };
    return messages[flowId] || 'Let\'s continue!';
  }
}

export const assistantManager = new AssistantManager();
